import { useMemo, useCallback } from 'react';
import { useAccount } from '../context/AccountContext';

const STORE_COLORS = ['#D4F0A5', '#FADBB0', '#FFE4E6', '#E0E7FF', '#FEF3C7'];
const LEGACY_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

/**
 * Hook para obtener las tiendas de la cuenta del usuario.
 *
 * La referencia canónica de una tienda es su `id` (store_xxxx, inmutable). El `code`
 * (corto, editable) y las letras legacy "A"/"B" solo se aceptan para leer datos
 * antiguos. Si la cuenta no tiene tiendas configuradas, `stores` es un array vacío
 * (no se inventan "Tienda A" / "Tienda B").
 */
export const useStores = () => {
  const { account } = useAccount();

  const stores = useMemo(() => {
    const raw = account?.stores || [];
    return raw
      .filter((store) => store.active !== false)
      .slice(0, account?.max_stores || raw.length)
      .map((store, index) => ({
        ...store,
        key: store.code || store.id, // compat: algunos componentes usan `key`
        color: STORE_COLORS[index % STORE_COLORS.length],
        active: store.active !== false,
      }));
  }, [account]);

  const storeOptions = useMemo(
    () => stores.map((store) => ({ value: store.id, label: store.name })),
    [stores]
  );

  const getStoreById = useCallback(
    (storeId) => stores.find((s) => s.id === storeId),
    [stores]
  );

  const getStoreByKey = useCallback(
    (key) => stores.find((s) => s.code === key || s.key === key),
    [stores]
  );

  /**
   * Devuelve el store_id canónico a partir de un valor que puede ser:
   * un store_id, un code, o una letra legacy ("A".."E").
   */
  const resolveStoreId = useCallback(
    (raw) => {
      if (!raw) return null;
      const byId = stores.find((s) => s.id === raw);
      if (byId) return byId.id;
      const byCode = stores.find((s) => s.code === raw);
      if (byCode) return byCode.id;
      const letterIdx = LEGACY_LETTERS.indexOf(raw);
      if (letterIdx >= 0 && letterIdx < stores.length) return stores[letterIdx].id;
      return null;
    },
    [stores]
  );

  const getStore = useCallback(
    (raw) => {
      const sid = resolveStoreId(raw);
      return sid ? stores.find((s) => s.id === sid) : undefined;
    },
    [stores, resolveStoreId]
  );

  /** Nombre configurado de la tienda. Si no resuelve, devuelve el valor crudo. */
  const getStoreName = useCallback(
    (raw) => {
      const store = getStore(raw);
      return store ? store.name : (raw || '');
    },
    [getStore]
  );

  const getStoreColor = useCallback(
    (raw) => {
      const store = getStore(raw);
      return store?.color || STORE_COLORS[0];
    },
    [getStore]
  );

  return {
    stores,
    storeOptions,
    getStore,
    getStoreById,
    getStoreByKey,
    getStoreName,
    getStoreColor,
    resolveStoreId,
    storeCount: stores.length,
  };
};
